import { OrderStatusType, PaymentStatusType } from "@models/order";
import { paymentStatusLabel } from "../_components/status";
import type { OrderTransitionAction } from "../_components/transitions";

// Fulfillment the API refuses (409) unless the order's payment is Captured
// (#442 / C-05). Delivery is not gated on Captured: goods already shipped are
// delivered whatever happens to the payment afterwards. It is refused only
// while an eCheck is pending settlement (below).
const FULFILLMENT_TARGETS: OrderStatusType[] = [
    OrderStatusType.Processing,
    OrderStatusType.Shipped,
];

// Why the API would refuse this transition given the order's payment status,
// or null when it would not. A refused action renders disabled with this
// reason beside it, rather than as a button that only produces a 409.
export const transitionBlockedReason = (
    action: OrderTransitionAction,
    paymentStatus: number | null,
): string | null => {
    // TransitionOrderCommand refuses an admin cancel while an eCheck is
    // pending settlement: the order waits for the settlement or the return.
    if (
        action.targetStatus === OrderStatusType.Cancelled &&
        paymentStatus === PaymentStatusType.PendingSettlement
    ) {
        return `${action.label} is unavailable while the payment is pending settlement. The order can be cancelled once the payment settles or is returned.`;
    }

    // TransitionOrderCommand also refuses delivery while an eCheck is pending
    // settlement. No current flow ships such an order (Processing waits for
    // Captured), but legacy rows can sit in Shipped at PendingSettlement.
    if (
        action.targetStatus === OrderStatusType.Delivered &&
        paymentStatus === PaymentStatusType.PendingSettlement
    ) {
        return `${action.label} is unavailable while the payment is pending settlement.`;
    }

    if (
        FULFILLMENT_TARGETS.includes(action.targetStatus) &&
        paymentStatus !== PaymentStatusType.Captured
    ) {
        // Keyed on the value: a missing status (null, or None) is "none" in
        // a sentence, whatever the label renders for it.
        const named =
            paymentStatus === null || paymentStatus === PaymentStatusType.None
                ? "none"
                : paymentStatusLabel(paymentStatus);
        return `${action.label} is unavailable until the payment is captured. Payment status: ${named}.`;
    }

    return null;
};
